'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

export default function ScrollReveal() {
  const pathname = usePathname();

  useEffect(() => {
    const root = document.documentElement;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = new Set<HTMLElement>();
    let observer: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const reveal = (element: HTMLElement) => {
      element.classList.add('is-revealed');
      observer?.unobserve(element);
    };

    const register = (element: HTMLElement) => {
      if (elements.has(element)) return;
      elements.add(element);

      if (reduceMotion || !observer) {
        reveal(element);
        return;
      }

      if (!element.style.getPropertyValue('--reveal-delay')) {
        const delay = element.dataset.revealDelay || '0ms';
        element.style.setProperty('--reveal-delay', delay);
      }

      observer.observe(element);
    };

    if (!('IntersectionObserver' in window) || reduceMotion) {
      document.querySelectorAll<HTMLElement>("[data-reveal='true']").forEach((element) => {
        element.classList.add('is-revealed');
      });
      return undefined;
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) reveal(entry.target as HTMLElement);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08,
      }
    );

    requestAnimationFrame(() => {
      root.classList.add('scroll-reveal-ready');
      document.querySelectorAll<HTMLElement>("[data-reveal='true']").forEach(register);
    });

    mutationObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches("[data-reveal='true']")) register(node);
          node.querySelectorAll<HTMLElement>("[data-reveal='true']").forEach(register);
        });
      });
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [pathname]);

  return null;
}
